import { useState, useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import { advisorChat, createTransactionsBulk, getCategories, getBalances } from '../api/client';

const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmt = (n) => `$${Math.abs(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SUGGESTED = [
  "Walk me through my bills for the rest of the month",
  "Is this a good week to move money to savings?",
  "Am I on track with my budget this period?",
  "Add these screenshot charges to my Capital One card",
];

const MAX_IMAGES = 3;

/** Compress image file → { media_type, data, previewUrl } */
const fileToCompressedImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = reject;
  reader.onload = () => {
    const img = new Image();
    img.onerror = reject;
    img.onload = () => {
      const maxW = 1600;
      const scale = Math.min(1, maxW / img.width);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.72);
      const data = dataUrl.split(',')[1] || '';
      resolve({ media_type: 'image/jpeg', data, previewUrl: dataUrl });
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const ImportReviewCard = ({ draft, COLORS, accounts, categories, onConfirm, onCancel, confirming }) => {
  const [lines, setLines] = useState(() =>
    (draft || []).map((l) => ({
      ...l,
      selected: l.status === 'ready' || l.status === 'cross_image_merged',
    }))
  );

  useEffect(() => {
    setLines((draft || []).map((l) => ({
      ...l,
      selected: l.status === 'ready' || l.status === 'cross_image_merged',
    })));
  }, [draft]);

  const toggle = (i) => setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, selected: !l.selected } : l));
  const setAccount = (i, accountId) => setLines((prev) => prev.map((l, idx) => {
    if (idx !== i) return l;
    const acc = accounts.find((a) => String(a.id) === String(accountId));
    return { ...l, account_id: accountId ? Number(accountId) : null, account_display: acc ? (acc.custom_name || acc.name) : null };
  }));
  const setCategory = (i, categoryId) => setLines((prev) => prev.map((l, idx) => {
    if (idx !== i) return l;
    const cat = categories.find((c) => String(c.id) === String(categoryId));
    return { ...l, category_id: categoryId ? Number(categoryId) : null, category_name: cat?.name || null };
  }));

  const selected = lines.filter((l) => l.selected && l.account_id);
  const statusColor = (s) => {
    if (s === 'already_in_ledger') return COLORS.textMuted;
    if (s === 'possible_duplicate' || s === 'needs_account') return COLORS.amber;
    return COLORS.green;
  };

  return (
    <div style={{ marginTop: 10, padding: 12, borderRadius: 12, background: COLORS.bg, border: `1px solid ${COLORS.border}` }}>
      <div style={{ color: COLORS.text, fontWeight: 700, fontSize: 13, marginBottom: 4 }}>Review import</div>
      <div style={{ color: COLORS.textMuted, fontSize: 11, marginBottom: 10 }}>
        Confirm to add selected charges. Already-in-ledger lines stay off unless you check them.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
        {lines.map((l, i) => (
          <div key={l.temp_id || i} style={{
            padding: 10, borderRadius: 10,
            border: `1px solid ${l.status === 'possible_duplicate' ? COLORS.amber + '66' : COLORS.border}`,
            background: l.status === 'already_in_ledger' ? 'transparent' : COLORS.surface,
            opacity: l.status === 'already_in_ledger' && !l.selected ? 0.7 : 1,
          }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!l.selected} onChange={() => toggle(i)} style={{ marginTop: 3 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {l.merchant_name || l.description}
                  </div>
                  <div style={{ color: (l.type === 'income' || l.type === 'transfer' || l.amount < 0) ? COLORS.green : COLORS.red, fontFamily: 'monospace', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                    {(l.type === 'income' || l.type === 'transfer' || l.amount < 0) ? '+' : '-'}{fmt(l.amount)}
                  </div>
                </div>
                <div style={{ color: statusColor(l.status), fontSize: 10, fontWeight: 600, marginTop: 3 }}>
                  {l.status === 'cross_image_merged' ? 'Merged from overlapping screenshots'
                    : l.status === 'already_in_ledger' ? 'Already in ledger'
                    : l.status === 'possible_duplicate' ? 'Possible duplicate'
                    : l.status === 'needs_account' ? 'Pick an account'
                    : 'Ready'}
                  {l.match_hint ? ` · ${l.match_hint}` : ''}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                  <select
                    value={l.account_id || ''}
                    onChange={(e) => setAccount(i, e.target.value)}
                    style={{ flex: 1, minWidth: 120, padding: '5px 8px', borderRadius: 6, border: `1px solid ${COLORS.border}`, background: COLORS.bg, color: COLORS.text, fontSize: 11, fontFamily: 'inherit' }}
                  >
                    <option value="">Account…</option>
                    {accounts.filter((a) => ['depository', 'credit'].includes(a.type)).map((a) => (
                      <option key={a.id} value={a.id}>{a.custom_name || a.name}</option>
                    ))}
                  </select>
                  <select
                    value={l.category_id || ''}
                    onChange={(e) => setCategory(i, e.target.value)}
                    style={{ flex: 1, minWidth: 120, padding: '5px 8px', borderRadius: 6, border: `1px solid ${COLORS.border}`, background: COLORS.bg, color: COLORS.text, fontSize: 11, fontFamily: 'inherit' }}
                  >
                    <option value="">Category…</option>
                    {[...categories].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div style={{ color: COLORS.textMuted, fontSize: 10, marginTop: 4 }}>{l.date} · {l.type}</div>
              </div>
            </label>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          type="button"
          disabled={confirming || selected.length === 0}
          onClick={() => onConfirm(selected)}
          style={{
            flex: 1, padding: '10px 12px', borderRadius: 10, border: 'none',
            background: selected.length && !confirming ? COLORS.accent : COLORS.border,
            color: '#fff', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
            cursor: selected.length && !confirming ? 'pointer' : 'not-allowed',
          }}
        >
          {confirming ? 'Adding…' : `Confirm ${selected.length}`}
        </button>
        <button
          type="button"
          disabled={confirming}
          onClick={onCancel}
          style={{
            padding: '10px 14px', borderRadius: 10, border: `1px solid ${COLORS.border}`,
            background: 'transparent', color: COLORS.textMuted, fontWeight: 600, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const Advisor = ({ messages = [], setMessages = () => {} }) => {
  const { COLORS } = useTheme();
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingImages, setPendingImages] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const started = messages.length > 0;
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const attachRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    Promise.all([getBalances(), getCategories()])
      .then(([bal, cats]) => {
        setAccounts(bal.data?.accounts || []);
        const d = cats.data;
        setCategories(Array.isArray(d) ? d : d?.data || []);
      })
      .catch(() => {});
  }, []);

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (!files.length) return;
    const room = MAX_IMAGES - pendingImages.length;
    if (room <= 0) return;
    const slice = files.slice(0, room);
    const compressed = await Promise.all(slice.map(fileToCompressedImage));
    setPendingImages((prev) => [...prev, ...compressed].slice(0, MAX_IMAGES));
  };

  const send = async (text) => {
    const contentText = (text || input).trim();
    if ((!contentText && pendingImages.length === 0) || loading) return;
    setInput('');

    const images = [...pendingImages];
    setPendingImages([]);

    const apiContent = [];
    if (contentText) apiContent.push({ type: 'text', text: contentText });
    images.forEach((img) => {
      apiContent.push({ type: 'image', media_type: img.media_type, data: img.data });
    });

    const userMsg = {
      role: 'user',
      content: apiContent.length === 1 && apiContent[0].type === 'text' ? contentText : apiContent,
      previewImages: images.map((i) => i.previewUrl),
      displayText: contentText || (images.length ? `Sent ${images.length} screenshot${images.length > 1 ? 's' : ''}` : ''),
    };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setLoading(true);

    try {
      // Strip preview URLs before API (server only needs content)
      const payload = nextMessages.map(({ role, content }) => ({ role, content }));
      const res = await advisorChat(payload, localToday());
      const reply = res.data?.reply || "Sorry, I couldn't process that.";
      const proposed = res.data?.proposed_transactions || null;
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: reply,
        proposed_transactions: proposed,
      }]);
    } catch (e) {
      setMessages((prev) => [...prev, { role: 'assistant', content: 'Something went wrong. Try again in a moment.' }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleConfirmImport = async (msgIndex, selected) => {
    setConfirming(true);
    try {
      const res = await createTransactionsBulk(selected.map((l) => ({
        date: l.date,
        amount: l.amount,
        type: l.type || 'expense',
        description: l.description || l.merchant_name,
        merchant_name: l.merchant_name,
        account_id: l.account_id,
        category_id: l.category_id || null,
      })));
      const created = res.data?.created_count || 0;
      const skipped = res.data?.skipped_count || 0;
      setMessages((prev) => {
        const next = [...prev];
        if (next[msgIndex]) {
          next[msgIndex] = { ...next[msgIndex], proposed_transactions: null, importDone: true };
        }
        next.push({
          role: 'assistant',
          content: `Added ${created} transaction${created === 1 ? '' : 's'}${skipped ? ` (skipped ${skipped} already in ledger)` : ''}. They count toward spend now and will be replaced when the bank posts them.`,
        });
        return next;
      });
    } catch (e) {
      setMessages((prev) => [...prev, { role: 'assistant', content: 'Could not save those transactions. Try again.' }]);
    } finally {
      setConfirming(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const canSend = (input.trim() || pendingImages.length > 0) && !loading;

  const avatar = (size) => (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: `linear-gradient(145deg, ${COLORS.accent}, ${COLORS.green})`,
      boxShadow: `inset 0 0 0 1px ${COLORS.border}`,
    }} />
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 130px)' }}>

      <div style={{ padding: '10px 14px', borderBottom: `1px solid ${COLORS.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {avatar(28)}
            <div>
              <div style={{ color: COLORS.text, fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>Penny</div>
              <div style={{ color: COLORS.textMuted, fontSize: 11 }}>Financial advisor</div>
            </div>
          </div>
          {started && (
            <button
              onClick={() => setMessages([])}
              style={{ padding: '5px 10px', background: 'transparent', border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, fontSize: 12 }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10, scrollbarWidth: 'none' }}>

        {!started && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 8 }}>
            {avatar(44)}
            <div style={{ color: COLORS.text, fontWeight: 700, fontSize: 16, marginTop: 10, marginBottom: 4 }}>Hi, I'm Penny</div>
            <div style={{ color: COLORS.textMuted, fontSize: 12, textAlign: 'center', lineHeight: 1.45, marginBottom: 14, maxWidth: 280 }}>
              Ask about your budget, or attach bank screenshots to draft charges for review.
            </div>
            <div style={{ width: '100%' }}>
              <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.7, textTransform: 'uppercase', marginBottom: 6 }}>Suggested</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {SUGGESTED.map((q, i) => (
                  <button key={i} onClick={() => send(q)}
                    style={{ padding: '9px 12px', background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, color: COLORS.text, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, textAlign: 'left', lineHeight: 1.35 }}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', gap: 8, alignItems: 'flex-end' }}>
            {msg.role === 'assistant' && avatar(26)}
            <div style={{ maxWidth: '88%' }}>
              <div style={{
                padding: '10px 14px',
                borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                background: msg.role === 'user' ? COLORS.accent : COLORS.surface,
                border: msg.role === 'user' ? 'none' : `1px solid ${COLORS.border}`,
                color: msg.role === 'user' ? '#fff' : COLORS.text,
                fontSize: 14,
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
              }}>
                {msg.displayText || (typeof msg.content === 'string' ? msg.content : '')}
                {msg.previewImages?.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, marginTop: msg.displayText ? 8 : 0, flexWrap: 'wrap' }}>
                    {msg.previewImages.map((src, j) => (
                      <img key={j} src={src} alt="" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(255,255,255,0.25)' }} />
                    ))}
                  </div>
                )}
              </div>
              {msg.role === 'assistant' && msg.proposed_transactions?.length > 0 && !msg.importDone && (
                <ImportReviewCard
                  draft={msg.proposed_transactions}
                  COLORS={COLORS}
                  accounts={accounts}
                  categories={categories}
                  confirming={confirming}
                  onCancel={() => setMessages((prev) => {
                    const next = [...prev];
                    next[i] = { ...next[i], proposed_transactions: null };
                    return next;
                  })}
                  onConfirm={(selected) => handleConfirmImport(i, selected)}
                />
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            {avatar(26)}
            <div style={{ padding: '10px 14px', background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '18px 18px 18px 4px', display: 'flex', gap: 4, alignItems: 'center' }}>
              {[0, 1, 2].map((i) => (
                <div key={i} style={{
                  width: 6, height: 6, borderRadius: '50%', background: COLORS.textMuted,
                  animation: 'pulse 1.2s ease-in-out infinite',
                  animationDelay: `${i * 0.2}s`,
                  opacity: 0.5,
                }} />
              ))}
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <div style={{ padding: '8px 12px 10px', borderTop: `1px solid ${COLORS.border}`, background: COLORS.bg }}>
        {pendingImages.length > 0 && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
            {pendingImages.map((img, i) => (
              <div key={i} style={{ position: 'relative' }}>
                <img src={img.previewUrl} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8, border: `1px solid ${COLORS.border}` }} />
                <button
                  type="button"
                  onClick={() => setPendingImages((prev) => prev.filter((_, j) => j !== i))}
                  style={{ position: 'absolute', top: -5, right: -5, width: 18, height: 18, borderRadius: 99, border: 'none', background: COLORS.red, color: '#fff', fontSize: 11, cursor: 'pointer', lineHeight: '18px', padding: 0 }}
                >×</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
          <input ref={attachRef} type="file" accept="image/*" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          <button
            type="button"
            title="Attach"
            aria-label="Attach screenshot"
            disabled={loading || pendingImages.length >= MAX_IMAGES}
            onClick={() => attachRef.current?.click()}
            style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              border: `1px solid ${COLORS.border}`, background: COLORS.surface,
              color: COLORS.text, cursor: pendingImages.length >= MAX_IMAGES ? 'not-allowed' : 'pointer',
              fontSize: 22, fontWeight: 400, lineHeight: 1, padding: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: pendingImages.length >= MAX_IMAGES ? 0.45 : 1,
            }}
          >+</button>
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Ask Penny…"
            rows={1}
            style={{
              flex: 1, padding: '9px 12px', background: COLORS.surface,
              border: `1px solid ${COLORS.border}`, borderRadius: 12, color: COLORS.text,
              fontFamily: 'inherit', fontSize: 14, resize: 'none', outline: 'none',
              lineHeight: 1.4, maxHeight: 100, overflowY: 'auto',
            }}
            onInput={(e) => {
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
            }}
          />
          <button
            onClick={() => send()}
            disabled={!canSend}
            style={{
              width: 36, height: 36, borderRadius: 10, border: 'none', flexShrink: 0,
              background: canSend ? COLORS.accent : COLORS.border,
              color: '#fff',
              cursor: canSend ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 700, transition: 'background 0.2s',
            }}
          >
            ↑
          </button>
        </div>
        <style>{`@keyframes pulse { 0%,100%{opacity:0.3} 50%{opacity:1} }`}</style>
      </div>
    </div>
  );
};

export default Advisor;
